import { Drawer } from '@/components/modules';
import { useSiteComprehensiveStatus } from '@/hooks';
import { useSiteDashboard } from '@/hooks/useSiteDashboard';
import { useSiteDetails } from '@/hooks/useSiteDetails';
import { useSiteErrorStatus } from '@/hooks/useSiteErrorStatus';
import { useSiteMotorStatus } from '@/hooks/useSiteMotorStatus';
import type { SiteData } from '@/types/site';
import { useMemo } from 'react';
import DrawerStatusBar from './components/DrawerStatusBar';
import MotorStatusSection from './components/MotorStatusSection';
import SiteInfoSection from './components/SiteInfoSection';
import WaterLevelChart from './components/WaterLevelChart';
import WaterTankSection from './components/WaterTankSection';

interface SiteDetailDrawerProps {
  selectedSite: SiteData | null;
  drawerExpanded: boolean;
  setDrawerExpanded: (expanded: boolean) => void;
  onClose: () => void;
}

export default function SiteDetailDrawer({
  selectedSite,
  drawerExpanded,
  setDrawerExpanded,
  onClose,
}: SiteDetailDrawerProps) {
  // API 데이터 훅들 - selectedSite가 있을 때만 활성화
  const siteId = selectedSite?.siteId || '';

  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    error: dashboardError,
    actions: { refetch: refetchDashboard },
  } = useSiteDashboard(siteId);

  const {
    data: comprehensiveStatusData,
    isLoading: isComprehensiveStatusLoading,
    error: comprehensiveStatusError,
    actions: { refetch: refetchComprehensiveStatus },
  } = useSiteComprehensiveStatus(siteId);

  // comprehensiveStatusData가 있으면 모터 상태 API 호출 비활성화
  const {
    isLoading: isMotorLoading,
    data: motorRunTimeData,
    actions: { refetch: refetchMotorStatus },
  } = useSiteMotorStatus(siteId);

  const { data: siteDetailsData, isLoading: isDetailsLoading } =
    useSiteDetails(siteId);

  const { data: errorData, isLoading: isErrorLoading } =
    useSiteErrorStatus(siteId);

  // 통합 로딩 상태
  const isLoading =
    isDashboardLoading ||
    isComprehensiveStatusLoading ||
    isMotorLoading ||
    isDetailsLoading ||
    isErrorLoading;

  // 모든 에러 상황을 적층해서 수집
  const allErrors = useMemo(() => {
    const errors: Array<{ type: string; message: string; source: string }> = [];

    const currentWaterLevel = comprehensiveStatusData?.waterLevel?.current || 0;
    const lowWaterLevel = comprehensiveStatusData?.waterLevel?.low || 0;
    const highWaterLevel = comprehensiveStatusData?.waterLevel?.high || 0;

    // API 호출 에러들
    if (dashboardError) {
      errors.push({
        type: 'api_error',
        message: `대시보드 데이터 로드 실패: ${dashboardError.message}`,
        source: 'dashboard_api',
      });
    }
    if (comprehensiveStatusError) {
      errors.push({
        type: 'api_error',
        message: `종합 상태 데이터 로드 실패: ${comprehensiveStatusError.message}`,
        source: 'comprehensive_status_api',
      });
    }

    // comprehensiveStatusData의 하드웨어 에러들
    if (comprehensiveStatusData?.errorStatus) {
      const { errorStatus } = comprehensiveStatusData;

      if (errorStatus.electricWaveError) {
        errors.push({
          type: 'hardware_error',
          message: '전파 통신 이상이 감지되었습니다',
          source: 'sensor_data',
        });
      }
      if (errorStatus.overloadError) {
        errors.push({
          type: 'hardware_error',
          message: '모터 과부하 상태입니다',
          source: 'sensor_data',
        });
      }
      if (errorStatus.undergroundWaterError) {
        errors.push({
          type: 'hardware_error',
          message: '지하수위 센서 이상입니다',
          source: 'sensor_data',
        });
      }
      if (currentWaterLevel < lowWaterLevel) {
        errors.push({
          type: 'hardware_error',
          message: '현재 수위가 저수위 이하입니다',
          source: 'sensor_data',
        });
      }
      if (currentWaterLevel > highWaterLevel) {
        errors.push({
          type: 'hardware_error',
          message: '현재 수위가 고수위 이상입니다',
          source: 'sensor_data',
        });
      }
    }

    // 기존 errorData의 에러들도 포함
    if (errorData?.errors) {
      errorData?.errors?.forEach((error) => {
        errors.push({
          type: error.type,
          message: error.message,
          source: 'legacy_api',
        });
      });
    }

    return errors;
  }, [dashboardError, comprehensiveStatusError, errorData?.errors]);

  // 실제 API 데이터와 fallback 데이터 병합 (comprehensiveStatusData 우선)
  const displayData = useMemo(() => {
    if (!selectedSite) return null;

    return {
      // 기본 정보 (selectedSite에서 가져오거나 API 데이터 사용)
      name: siteDetailsData?.name || selectedSite.name,
      address: siteDetailsData?.position?.address || selectedSite.address,
      status: siteDetailsData?.status || selectedSite.status,
      installationDate:
        siteDetailsData?.installedDate || selectedSite.installationDate,
      lastInspection: siteDetailsData?.lastInspection || {
        date: selectedSite.lastInspection,
        title: '정기 점검',
      },

      // 모터 데이터 (comprehensiveStatusData 우선)
      motorStatus: comprehensiveStatusData?.motorStatus?.mainMotor
        ? '운전'
        : dashboardData?.motor?.currentStatus
          ? dashboardData.motor.currentStatus
            ? '운전'
            : '정지'
          : selectedSite.motorOperation,
      motorRunningTime:
        dashboardData?.motor?.statusContinuesMinutes ||
        selectedSite.motorRunningTime,
      motorStoppedTime: selectedSite.motorStoppedTime,
      cumulativeHours:
        dashboardData?.motor?.runTime?.total || selectedSite.cumulativeHours,
      todayHours:
        dashboardData?.motor?.runTime?.today ||
        selectedSite.cumulativeHoursToday,
      dailyHours: dashboardData?.motor?.runTime?.yesterday || 0,
      weeklyHours: dashboardData?.motor?.runTime?.weekly || 0,
      monthlyHours: dashboardData?.motor?.runTime?.monthly || 0,

      // 수위 데이터 (comprehensiveStatusData 우선)
      waterLevel:
        comprehensiveStatusData?.waterLevel?.current ||
        dashboardData?.waterLevel?.current ||
        selectedSite.waterLevel,

      // 추가 센서 데이터 (comprehensiveStatusData에서만 제공)
      temperature: comprehensiveStatusData?.temperature || 0,
      inflow: comprehensiveStatusData?.inflow || 0,
      accumulatedPulseValue:
        comprehensiveStatusData?.accumulatedPulseValue || 0,
      undergroundWaterLevel:
        comprehensiveStatusData?.undergroundWaterLevel || 0,

      cumulativeFlow: siteDetailsData?.cumulativeInflow
        ? `${siteDetailsData.cumulativeInflow}m³`
        : selectedSite.cumulativeFlow,

      // 차트 데이터 (기존 fallback 유지)
      waterLevelTrend: selectedSite.waterLevelTrend,

      // 통합된 에러 데이터
      hasError: allErrors.length > 0,
      errorCount: allErrors.length,
      errors: allErrors,
    };
  }, [
    selectedSite,
    comprehensiveStatusData,
    dashboardData,
    siteDetailsData,
    allErrors,
  ]);

  // API 에러가 발생한 경우 에러 상태 표시
  const hasApiError = dashboardError || comprehensiveStatusError;

  if (!selectedSite || !displayData) return null;

  return (
    <Drawer
      isOpen={!!selectedSite}
      onClose={onClose}
      title={displayData.address}
      subtitle={`현재 수위: ${displayData.waterLevel}%${hasApiError ? ' (데이터 오류)' : ''}${isLoading ? ' (업데이트 중...)' : ''}`}
      expandable={true}
      expanded={drawerExpanded}
      onExpandToggle={() => setDrawerExpanded(!drawerExpanded)}
      width="w-[33vw]"
    >
      {drawerExpanded ? (
        <>
          {/* 상태 표시 바 */}
          <DrawerStatusBar
            hasApiError={!!hasApiError}
            isLoading={isLoading}
            onRefresh={() => {
              refetchDashboard();
              refetchComprehensiveStatus();
              refetchMotorStatus();
            }}
            expanded={true}
          />

          {/* 주요 섹션들 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <MotorStatusSection
              motorActivateCount={selectedSite.motorActivateCount || 0}
              motorRunTimeData={
                motorRunTimeData?.runTime || {
                  today: 0,
                  yesterday: 0,
                  weekly: 0,
                  monthly: 0,
                  total: 0,
                }
              }
              motorStatus={
                comprehensiveStatusData?.motorStatus || {
                  mainMotor: false,
                  motor1: false,
                  motor2: false,
                  motor3: false,
                }
              }
              data={{
                motorStatus: displayData.motorStatus,
                motorRunningTime: displayData.motorRunningTime,
                motorStoppedTime: displayData.motorStoppedTime,
                cumulativeHours: displayData.cumulativeHours,
                todayHours: displayData.todayHours,
                dailyHours: displayData.dailyHours,
                weeklyHours: displayData.weeklyHours,
              }}
              isLoading={isLoading}
              expanded={true}
            />

            {/* 과부하, 지하수 */}
            <WaterTankSection
              waterLevel={comprehensiveStatusData?.waterLevel?.current || 0}
              chemicalLevel={comprehensiveStatusData?.chemicalLevel}
              undergroundWaterLevel={
                comprehensiveStatusData?.undergroundWaterLevel
              }
              temperature={comprehensiveStatusData?.temperature}
              inflow={comprehensiveStatusData?.inflow}
              isLoading={isComprehensiveStatusLoading}
              pressureSensorError={
                comprehensiveStatusData?.errorStatus?.pressureSensorError ||
                false
              }
              electricWaveError={
                comprehensiveStatusData?.errorStatus?.electricWaveError || false
              }
              externalError={
                comprehensiveStatusData?.errorStatus?.externalError || false
              }
              circuitBreakerError={
                comprehensiveStatusData?.errorStatus?.circuitBreakerError ||
                false
              }
              overloadError={
                comprehensiveStatusData?.errorStatus?.overloadError || false
              }
              undergroundWaterError={
                comprehensiveStatusData?.errorStatus?.undergroundWaterError ||
                false
              }
              alias={
                selectedSite.alias || {
                  circuitBreaker: '차단기',
                  pressureSensor: '압력센서',
                  external: '외부',
                  preparation: '예비1',
                  preparation2: '예비2',
                }
              }
              preparation={
                comprehensiveStatusData?.errorStatus?.preparation || false
              }
              preparation2={
                comprehensiveStatusData?.errorStatus?.preparation2 || false
              }
            />

            <SiteInfoSection
              data={{
                name: displayData.name,
                status: displayData.status,
                cumulativeFlow: displayData.cumulativeFlow,
                installationDate: displayData.installationDate,
                lastInspection: displayData.lastInspection,
                hasError: displayData.hasError,
                errorCount: displayData.errorCount,
                errors: displayData.errors,
              }}
              expanded={true}
            />
          </div>

          {/* 차트 섹션 */}
          {
            <div className="mt-8">
              <WaterLevelChart
                siteId={siteId}
                initialPeriod="daily"
                isFullScreen={true}
              />
            </div>
          }
        </>
      ) : (
        <>
          {/* 축소된 뷰 */}
          <DrawerStatusBar
            hasApiError={!!hasApiError}
            isLoading={isLoading}
            onRefresh={() => {
              refetchDashboard();
              refetchComprehensiveStatus();
              refetchMotorStatus();
            }}
            expanded={false}
          />

          <div className="space-y-6">
            <MotorStatusSection
              motorRunTimeData={
                motorRunTimeData?.runTime || {
                  today: 0,
                  yesterday: 0,
                  weekly: 0,
                  monthly: 0,
                  total: 0,
                }
              }
              motorActivateCount={selectedSite.motorActivateCount || 1}
              motorStatus={
                comprehensiveStatusData?.motorStatus || {
                  mainMotor: false,
                  motor1: false,
                  motor2: false,
                  motor3: false,
                }
              }
              data={{
                motorStatus: displayData.motorStatus,
                motorRunningTime: displayData.motorRunningTime,
                motorStoppedTime: displayData.motorStoppedTime,
                cumulativeHours: displayData.cumulativeHours,
                todayHours: displayData.todayHours,
                dailyHours: displayData.dailyHours,
                weeklyHours: displayData.weeklyHours,
              }}
              isLoading={isLoading}
              expanded={false}
            />
            <WaterTankSection
              waterLevel={comprehensiveStatusData?.waterLevel?.current || 0}
              chemicalLevel={comprehensiveStatusData?.chemicalLevel}
              undergroundWaterLevel={
                comprehensiveStatusData?.undergroundWaterLevel
              }
              temperature={comprehensiveStatusData?.temperature}
              inflow={comprehensiveStatusData?.inflow}
              isLoading={isComprehensiveStatusLoading}
              pressureSensorError={
                comprehensiveStatusData?.errorStatus?.pressureSensorError ||
                false
              }
              electricWaveError={
                comprehensiveStatusData?.errorStatus?.electricWaveError || false
              }
              externalError={
                comprehensiveStatusData?.errorStatus?.externalError || false
              }
              circuitBreakerError={
                comprehensiveStatusData?.errorStatus?.circuitBreakerError ||
                false
              }
              overloadError={
                comprehensiveStatusData?.errorStatus?.overloadError || false
              }
              undergroundWaterError={
                comprehensiveStatusData?.errorStatus?.undergroundWaterError ||
                false
              }
              alias={
                selectedSite.alias || {
                  circuitBreaker: '차단기',
                  pressureSensor: '압력센서',
                  external: '외부',
                  preparation: '예비1',
                  preparation2: '예비2',
                }
              }
              preparation={
                comprehensiveStatusData?.errorStatus?.preparation || false
              }
              preparation2={
                comprehensiveStatusData?.errorStatus?.preparation2 || false
              }
            />

            <>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                수위 추이
              </h3>
              <WaterLevelChart
                siteId={siteId}
                initialPeriod="daily"
                isFullScreen={false}
              />
            </>

            <SiteInfoSection
              data={{
                name: displayData.name,
                status: displayData.status,
                cumulativeFlow: displayData.cumulativeFlow,
                installationDate: displayData.installationDate,
                lastInspection: displayData.lastInspection,
                hasError: displayData.hasError,
                errorCount: displayData.errorCount,
                errors: displayData.errors,
              }}
              expanded={false}
            />
          </div>
        </>
      )}
    </Drawer>
  );
}
